import { useRef, useState } from 'react';
import type { ChatUiFlightOffer } from '@voll-intelligence/genui-contract';
import { XIcon } from '@phosphor-icons/react';
import { Sheet, SheetContent } from '@voll-intelligence/ui-kit/ui/sheet';
import { useGenUiI18n, type GenUiKey } from '../../../../shared/ui/i18n.js';
import { usePushDrawerMarker } from '@voll-intelligence/intelligence-ui-kit';
import {
  defaultFilterState,
  filterAndSortOffers,
  type FlightFilterState,
  type SortKey,
} from '@voll-intelligence/intelligence-ui-kit';
import { FlightFilterPanel } from '../../../shared/ui/filter_panel.js';
import { activeAxes } from '@voll-intelligence/intelligence-ui-kit';
import { useFilterEmptyHint } from '../../../shared/empty_hint.js';
import { useOfferLayout } from '@voll-intelligence/intelligence-ui-kit';
import { FlightOfferRow } from './offer_row.js';
import { FlightPolicyDrawer } from './policy_drawer.js';
import '@voll-intelligence/intelligence-ui-kit/flight-drawer.css';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../../shared/ui/flight_ui.css';

/**
 * Drawer de filtro de voos (voltada ao cliente): abre a partir do CTA abaixo do
 * carrossel e filtra/ordena a lista COMPLETA (`allOffers`) ali mesmo. A lista
 * exibida é derivada de `FlightFilterState` (sem estado duplicado) via
 * `filterAndSortOffers` — não refaz busca. Selecionar um voo reusa o canal de
 * quick-reply do chat (cotação não é ação com challengeId).
 *
 * Os EIXOS do filtro são o `FlightFilterPanel` unificado (`.ffp-*`) — o mesmo
 * da coluna da bancada determinística. O drawer mantém a casca (`.fd-*`), as
 * abas de ordenação e a lista de resultados; o "Redefinir" vive no painel.
 *
 * Abertura: EMPURRA a conversa (push), igual ao detalhe do voo — marca
 * `:root[data-push-drawer-open]` (via `usePushDrawerMarker`). A casca é o `Sheet`
 * do DS não-modal (`medium`, 576): sem véu, e o chat segue interativo ao lado.
 */
const SORTS: ReadonlyArray<{ key: SortKey; label: GenUiKey }> = [
  { key: 'price', label: 'flightFilter.sort.price' },
  { key: 'dep', label: 'flightFilter.sort.dep' },
  { key: 'dur', label: 'flightFilter.sort.dur' },
];

export function FlightFilterDrawer({
  offers,
  onSelect,
  onClose,
}: {
  readonly offers: ChatUiFlightOffer[];
  readonly onSelect: (offer: ChatUiFlightOffer) => void;
  readonly onClose: () => void;
}) {
  const { t } = useGenUiI18n();
  // Default PRÓPRIO ('price'), não o do explorer ('rec'): as ofertas deste
  // drawer (`allOffers`) viajam sem badges por contrato do wire, então
  // "Recomendado" aqui não teria aba nem critério a desenhar.
  const [st, setSt] = useState<FlightFilterState>(() => ({
    ...defaultFilterState(offers),
    sort: 'price',
  }));
  const [policyOffer, setPolicyOffer] = useState<ChatUiFlightOffer | null>(null);
  const { ref: listRef, layout } = useOfferLayout<HTMLUListElement>();
  const closeRef = useRef<HTMLButtonElement>(null);
  const rows = filterAndSortOffers(offers, st);
  const emptyHint = useFilterEmptyHint(activeAxes(offers, st));
  const route = offers[0]?.segments[0];

  // Empurra a conversa (push) enquanto aberto — mesmo contrato do detalhe do voo.
  usePushDrawerMarker();

  return (
    <Sheet
      open
      modal={false}
      // A conversa ao lado segue clicável: clicar nela não fecha o painel.
      disablePointerDismissal
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        size="medium"
        showCloseButton={false}
        aria-label={t('flightFilter.aria')}
        initialFocus={closeRef}
        className="fd-panel gap-0"
      >
        <header className="fd-top">
          <div>
            <h3 className="fd-ttl">
              {t('flightFilter.title')} {route ? `${route.from} → ${route.to}` : ''}
            </h3>
            <p className="fd-sub">
              {t(offers.length === 1 ? 'flightFilter.option_one' : 'flightFilter.option_other', {
                count: offers.length,
              })}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="fd-close"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <XIcon size={16} aria-hidden />
          </button>
        </header>

        <div className="fd-scroll scrollbar-rail [scrollbar-gutter:auto]!">
          <div className="fd-group">
            <span className="fd-glabel">{t('flightFilter.sort.label')}</span>
            <fieldset
              className="fd-tabs"
              aria-label={t('flightFilter.sort.label')}
              style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
            >
              {SORTS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className={`fd-tab${st.sort === o.key ? ' is-active' : ''}`}
                  aria-pressed={st.sort === o.key}
                  onClick={() => {
                    setSt((s) => ({ ...s, sort: o.key }));
                  }}
                >
                  {t(o.label)}
                </button>
              ))}
            </fieldset>
          </div>

          <FlightFilterPanel offers={offers} state={st} onChange={setSt} variant="drawer" />

          <div className="fd-results">
            {rows.length === 0 ? (
              <div className="fd-empty">
                <p>{t('flightFilter.empty.title')}</p>
                {/* `status`: a dica muda com o filtro enquanto a lista segue vazia; sem
                    a região viva o leitor de tela não ouve a troca. */}
                <p className="fd-empty-hint" role="status">
                  {emptyHint}
                </p>
              </div>
            ) : (
              <ul
                ref={listRef}
                className="fq-list fq-list--flight @container grid gap-6"
                aria-label={t('flightFilter.filteredAria')}
              >
                {rows.map((o, i) => (
                  <FlightOfferRow
                    key={o.offerId}
                    layout={layout}
                    // A gaveta é do card do chat: nada em volta diz o dia.
                    variant="chat"
                    offer={o}
                    position={i + 1}
                    // Pick one-way: a linha inteira seleciona o voo (reusa o canal
                    // de quick-reply). Sem `expanded` ⇒ sem CTA de tarifas — o
                    // drawer não expande (one-way, sem port/challengeId).
                    onToggleFares={(picked) => {
                      onSelect(picked);
                    }}
                    onOpenPolicy={setPolicyOffer}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="fd-foot">
          <span className="fd-count">
            {t(rows.length === 1 ? 'flightFilter.found_one' : 'flightFilter.found_other', {
              count: rows.length,
            })}
          </span>
        </div>
        {policyOffer && (
          <FlightPolicyDrawer
            offer={policyOffer}
            onClose={() => {
              setPolicyOffer(null);
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
